import { formatAED } from "./money.ts";
import { formatDayKey } from "./time.ts";
import type {
  AssistantAction,
  AssistantChatStatus,
  AssistantColumn,
  AssistantConversation,
  AssistantProposalBlock,
  AssistantTableBlock,
} from "./schemas/assistant.ts";

/**
 * The pure view rules of an AI Assistant chat (strategy repo `teamna/strategy/ai-assistant-v1.md`),
 * for Desk web and the mobile app: how a cell reads, the status words, the list filters, which plan
 * the decision bar confirms, and when an answer reads right to left. The server built the numbers,
 * so nothing here counts, adds or rounds.
 */

// ---------------------------------------------------------------------------
// Direction
// ---------------------------------------------------------------------------

/**
 * An Arabic letter: the letters of the Arabic blocks, without the digits, the punctuation and the
 * marks above and below a letter. Explicit ranges, because Hermes (the app) has no `\p{Script=...}`.
 */
function isArabicLetter(code: number): boolean {
  return (
    (code >= 0x0620 && code <= 0x064a) ||
    (code >= 0x066e && code <= 0x066f) ||
    (code >= 0x0671 && code <= 0x06d3) ||
    code === 0x06d5 ||
    (code >= 0x06ee && code <= 0x06ef) ||
    (code >= 0x06fa && code <= 0x06fc) ||
    code === 0x06ff ||
    (code >= 0x0750 && code <= 0x077f) ||
    (code >= 0x08a0 && code <= 0x08c9) ||
    (code >= 0xfb50 && code <= 0xfdfb) ||
    (code >= 0xfe70 && code <= 0xfefc)
  );
}

/** The share of the letters of `text` that are Arabic script, 0 to 1. A text with no letters is 0. */
export function arabicShare(text: string): number {
  let letters = 0;
  let arabic = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (isArabicLetter(code)) {
      letters += 1;
      arabic += 1;
    } else if (char.toLowerCase() !== char.toUpperCase()) {
      // A letter with a case: Latin, Greek, Cyrillic.
      letters += 1;
    }
  }
  return letters === 0 ? 0 : arabic / letters;
}

/** More than half of the letters are Arabic script: the text reads right to left. */
export function isMostlyArabic(text: string): boolean {
  return arabicShare(text) > 0.5;
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

export type AssistantCell = string | number | null;


/**
 * One cell as text. `formatDateTime` is the date and time format of the screen (Desk and the app
 * each have their own), for a `datetime` column.
 */
export function formatAssistantCell(
  value: AssistantCell,
  kind: AssistantColumn["kind"],
  timezone: string,
  formatDateTime: (iso: string, timezone: string) => string,
): string {
  if (value === null || value === "") return "";
  if (typeof value === "string" && kind !== "datetime" && kind !== "date") return value;
  switch (kind) {
    case "money":
      return typeof value === "number" ? formatAED(value) : value;
    case "percent":
      return `${value}%`;
    case "date":
      // A total row names itself ("Total") in the first column, even a date column.
      return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDayKey(value) : String(value);
    case "datetime":
      return typeof value === "string" ? formatDateTime(value, timezone) : String(value);
    default:
      return String(value);
  }
}

/** Numbers, money and percents align to the end of the cell. */
export function isAssistantNumericColumn(kind: AssistantColumn["kind"]): boolean {
  return kind === "number" || kind === "money" || kind === "percent";
}

/** The first text column names the row (the name, the group), so a phone keeps it. */
export function assistantTableNameKey(block: AssistantTableBlock): string | undefined {
  return block.columns.find((column) => column.kind === "text")?.key;
}

/** The total row as one line: "Total · Paid AED 1,200 · Sessions 14". Null when the table has no total. */
export function assistantTableTotalLine(
  block: AssistantTableBlock,
  timezone: string,
  formatDateTime: (iso: string, timezone: string) => string,
): string | null {
  const total = block.total;
  if (!total) return null;
  return block.columns
    .filter((column) => total[column.key] !== null && total[column.key] !== undefined)
    .map((column, index) =>
      index === 0 && !isAssistantNumericColumn(column.kind)
        ? formatAssistantCell(total[column.key]!, column.kind, timezone, formatDateTime)
        : `${column.label} ${formatAssistantCell(total[column.key]!, column.kind, timezone, formatDateTime)}`,
    )
    .join(" · ");
}

// ---------------------------------------------------------------------------
// Chat list
// ---------------------------------------------------------------------------

/** The status words of a chat. Amber waits for the person, violet is new, green is done. */
export const ASSISTANT_CHAT_STATUS_LABELS: Record<AssistantChatStatus, string> = {
  waiting: "Waiting for you",
  new: "New",
  open: "Open",
  done: "Done",
  snoozed: "Not now",
  closed: "Closed",
};

export type AssistantChatFilter = "all" | "waiting" | "new" | "open" | "done";

export const ASSISTANT_CHAT_FILTERS: { key: AssistantChatFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "waiting", label: "Waiting for you" },
  { key: "new", label: "New" },
  { key: "open", label: "Open" },
  { key: "done", label: "Done" },
];

/** "Done" also holds the closed chats; "Not now" shows only under "All". */
export function assistantChatInFilter(status: AssistantChatStatus, filter: AssistantChatFilter): boolean {
  if (filter === "all") return true;
  if (filter === "done") return status === "done" || status === "closed";
  return status === filter;
}

/** The example questions of a new chat. `participants` is the business's word, plural and capital. */
export function assistantExamplePrompts(participants: string): string[] {
  return [
    "Who attended last month?",
    `Top attendance and top absence last month, by ${participants.toLowerCase()}`,
    "Who must pay in the next 7 days?",
    "Who is overdue?",
    "What sessions are on tomorrow?",
  ];
}

// ---------------------------------------------------------------------------
// Plans and the decision bar
// ---------------------------------------------------------------------------

/** What happened to a plan that is not on the table any more. */
export const ASSISTANT_ACTION_STATUS_TEXT: Partial<Record<AssistantAction["status"], string>> = {
  replaced: "Replaced by a newer plan",
  confirmed: "Confirmed",
  stopped: "Stopped. The chat ended.",
  stale: "Not done. The data changed.",
  failed: "Not done. It failed.",
};

/** The proposal blocks of the chat, by action id. */
export function assistantProposals(conversation: AssistantConversation): Map<string, AssistantProposalBlock> {
  const out = new Map<string, AssistantProposalBlock>();
  for (const turn of conversation.turns) {
    for (const block of turn.blocks) if (block.type === "proposal") out.set(block.actionId, block);
  }
  return out;
}

export type AssistantProposalView = {
  action: AssistantAction | undefined;
  /** On the table: it shows in full. */
  live: boolean;
  /** How many plans are on the table in the same choice. More than 1 shows "Pick this". */
  options: number;
  /** Another option of the same choice was confirmed: this one is "Not picked", not "Replaced". */
  notPicked: boolean;
  /** The line for a plan that is not on the table any more. */
  statusText: string | undefined;
};

/** How one plan of the chat shows. Only the plans of the chat count, so the Control Center copy of a chat works too. */
export function assistantProposalView(conversation: Pick<AssistantConversation, "actions">, block: AssistantProposalBlock): AssistantProposalView {
  const actions = new Map(conversation.actions.map((action) => [action.id, action]));
  const action = actions.get(block.actionId);
  const by = action?.replacedById ? actions.get(action.replacedById) : undefined;
  const options = conversation.actions.filter((item) => item.status === "proposed" && item.optionGroup === block.optionGroup).length;
  const notPicked = by !== undefined && by.status === "confirmed" && by.optionGroup === action?.optionGroup;
  return {
    action,
    live: action?.status === "proposed",
    options,
    notPicked,
    statusText: notPicked ? "Not picked" : action ? ASSISTANT_ACTION_STATUS_TEXT[action.status] : undefined,
  };
}

export type AssistantDecisionBarView = {
  /** Plans on the table. */
  live: AssistantAction[];
  /** The plan Confirm confirms: the single live plan, or the picked option. Null: pick one first. */
  chosen: AssistantAction | null;
  /** The Confirm label: the `cta` of the chosen plan, else "Pick an option above". Null: no Confirm. */
  confirmLabel: string | null;
  /** A suggestion chat that is not finished: Not now and Close show. */
  canEnd: boolean;
};

/** The picked option is not on the table any more (confirmed, replaced, stopped): clear the pick. */
export function assistantPickIsStale(conversation: AssistantConversation, picked: string | null): boolean {
  return picked !== null && !conversation.actions.some((action) => action.id === picked && action.status === "proposed");
}

/** The decision bar of a chat, or null when it shows nothing. */
export function assistantDecisionBar(conversation: AssistantConversation, picked: string | null): AssistantDecisionBarView | null {
  const live = conversation.actions.filter((action) => action.status === "proposed");
  const chosen = live.length === 1 ? live[0]! : (live.find((action) => action.id === picked) ?? null);
  const chosenBlock = chosen ? assistantProposals(conversation).get(chosen.id) : undefined;
  const suggestion = conversation.cardKind !== null;
  const ended = conversation.status === "done" || conversation.status === "closed" || conversation.status === "snoozed";
  if (live.length === 0 && (!suggestion || ended)) return null;
  return {
    live,
    chosen,
    confirmLabel: live.length > 0 ? (chosenBlock ? chosenBlock.cta : "Pick an option above") : null,
    canEnd: suggestion && !ended,
  };
}
